import { PageTitle } from '@/components/ui/PageTitle';
import { DataTable } from '@/components/data-table/DataTable';
import { Transaction } from '@/components/ui/TransactionReceiptModal';
import {
	SessionAdminFilters,
	SessionAdminSearchBar,
} from './components/SessionAdminFilters';
import { SessionColumns } from './components';
import { useCreateSession, useGetSessions } from '@/services';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui';
import { useModal } from '@/hooks';
import {
	EditSessionModal,
	EditSessionDisplayNameModal,
} from './components/editModal';
import { AdminSessionDetailModal } from './components/AdminSessionDetailModal';
import { SessionAuditModal } from './components/SessionAuditModal';
import { SessionWhatsAppModal } from './components/SessionWhatsAppModal';
import { ReturnSessionModal } from './components/Return';
import { useQueryClient } from '@tanstack/react-query';
import {
	invalidateSessionsQueries,
	upsertSessionInSessionsCache,
} from '@/utils/sessionQueryCache';
import { formatProfessionalFullName } from '@/utils';
import {
	buildSessionAdminApiParams,
	parseSessionAdminUrlFilters,
} from './utils/sessionAdminFilters';
import { getBackendErrorMessage } from '@/utils/errorMessage';

const Sessions = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { openModal, setOnConfirm, setConfirmText, setCancelText } =
		useModal();
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [selectedReturnDate, setSelectedDate] = useState({
		available_date_id: '',
		is_online: true,
		selected_date: '',
	});
	const [auditSession, setAuditSession] = useState<Sessions.Session | null>(
		null,
	);
	const [whatsAppSession, setWhatsAppSession] =
		useState<Sessions.Session | null>(null);
	const selectedReturnDateRef = useRef(selectedReturnDate);

	const queryClient = useQueryClient();

	const urlFilters = useMemo(
		() => parseSessionAdminUrlFilters(searchParams),
		[searchParams],
	);

	const apiParams = useMemo(
		() => buildSessionAdminApiParams(urlFilters, { page, limit }),
		[urlFilters, page, limit],
	);

	const {
		data: sessionData,
		isLoading,
		isFetching,
		isError,
		error,
	} = useGetSessions(apiParams);

	const { mutate: mutateCreate } = useCreateSession();

	const filtersKey = useMemo(() => searchParams.toString(), [searchParams]);

	useEffect(() => {
		setPage(1);
	}, [filtersKey]);

	useEffect(() => {
		selectedReturnDateRef.current = selectedReturnDate;
	}, [selectedReturnDate]);

	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleView = (session: Sessions.Session) => {
		openModal({
			content: <AdminSessionDetailModal session={session} />,
			title: <h3 className="text-dark">Detalhes da consulta</h3>,
			size: 'xlarge',
			mobileSheet: 'tall',
		});
	};

	const handleEdit = (session: Sessions.Session) => {
		openModal({
			content: <EditSessionModal session={session} />,
			title: <h3 className="text-dark">Editar consulta</h3>,
		});
	};

	const handleEditDisplayName = (session: Sessions.Session) => {
		openModal({
			content: <EditSessionDisplayNameModal session={session} />,
			title: <h3 className="text-dark">Editar nome da sessão</h3>,
		});
	};

	const handleViewAudit = (session: Sessions.Session) => {
		setAuditSession(session);
	};

	const handleViewWhatsApp = (session: Sessions.Session) => {
		setWhatsAppSession(session);
	};

	const handleShowParticipants = (session: Sessions.Session) => {
		navigate(`/admin/consultas/${session.id}/participantes`, {
			state: { session },
		});
	};

	const handleUpdateTransaction = (
		_transactionId: string,
		_updates: Partial<Transaction>,
		updatedSession?: Sessions.Session,
	) => {
		if (updatedSession) {
			upsertSessionInSessionsCache(queryClient, updatedSession);
		}
		void invalidateSessionsQueries(queryClient);
	};

	const handleOnConfirmReturn = (data: Sessions.Session) => {
		const primaryUserId = data.user_id ?? data.participants?.[0]?.user_id;
		if (!primaryUserId) return;
		const currentDate = selectedReturnDateRef.current;
		const returnSessionData: Sessions.Create = {
			professional_id: data.professional.id,
			type: 'RETURN',
			parent_session: data.id,
			service_id: data.service_id,
			user_ids: [primaryUserId],
			...currentDate,
		};
		mutateCreate(returnSessionData);
	};

	const handleOnSessionReturn = (session: Sessions.Session) => {
		setConfirmText('Criar retorno');
		setCancelText('Cancelar');

		openModal({
			content: (
				<ReturnSessionModal
					professional={session.professional}
					setSelectedReturnDate={setSelectedDate}
				/>
			),
			title: (
				<h3 className="text-dark">{`Marcar retorno com ${formatProfessionalFullName(
					session.professional,
				)}`}</h3>
			),
		});

		setOnConfirm(() => handleOnConfirmReturn(session));
	};

	const listErrorMessage = isError ? getBackendErrorMessage(error) : null;

	const showInitialLoading = isLoading && !sessionData;
	const showEmptyState =
		!showInitialLoading &&
		!isError &&
		(sessionData?.data.length ?? 0) === 0;

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div className="space-y-1">
					<PageTitle text="Consultas" />
					<p className="text-sm text-stone-400">
						Gerencie as consultas e agendamentos.
					</p>
				</div>
				<Button
					text="Nova consulta"
					className="w-full shrink-0 rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
					startIcon={
						<Plus className="size-5 stroke-[3] text-white" />
					}
					onClick={() => navigate('/admin/consultas/agendar')}
				/>
			</div>

			<SessionAdminSearchBar />

			<div className="w-full min-w-0">
				<SessionAdminFilters />
			</div>

			{listErrorMessage ? (
				<p className="text-sm text-red-600" role="alert">
					{listErrorMessage}
				</p>
			) : null}

			{showInitialLoading ? (
				<div className="flex items-center justify-center gap-2 rounded-2xl border border-stone-200 bg-white py-16 text-sm text-stone-500">
					<Loader2 className="size-5 animate-spin" aria-hidden />
					Carregando consultas…
				</div>
			) : sessionData ? (
				<div className="space-y-2 pt-2">
					{isFetching ? (
						<p className="text-xs text-stone-400">
							Atualizando lista…
						</p>
					) : null}
					{showEmptyState ? (
						<p className="rounded-2xl border border-dashed border-stone-200 bg-white px-4 py-12 text-center text-sm text-stone-500">
							Nenhuma consulta encontrada para os filtros
							selecionados.
						</p>
					) : (
						<DataTable
							columns={SessionColumns({
								onView: handleView,
								onEdit: handleEdit,
								onEditDisplayName: handleEditDisplayName,
								onReturn: handleOnSessionReturn,
								onShowParticipants: handleShowParticipants,
								onViewAudit: handleViewAudit,
								onViewWhatsApp: handleViewWhatsApp,
								onUpdateTransaction: handleUpdateTransaction,
							})}
							data={sessionData.data}
							page={page}
							limit={limit}
							totalItems={sessionData.total}
							onPageChange={handlePageChange}
							onLimitChange={handleLimitChange}
						/>
					)}
				</div>
			) : null}
			<SessionAuditModal
				isOpen={auditSession != null}
				onClose={() => setAuditSession(null)}
				session={auditSession}
			/>
			<SessionWhatsAppModal
				isOpen={whatsAppSession != null}
				onClose={() => setWhatsAppSession(null)}
				session={whatsAppSession}
			/>
		</section>
	);
};

export { Sessions };
